/**
 * A whole list screen: page bar with saved-view tabs, metric cards, filter
 * chips, the controls row, the DataList itself, and export/import.
 *
 * Plan §3.5 describes this as one component reused by Jobs, Visits, Contacts,
 * Properties, Invoices, Equipment, Purchasing, Employees and Clients — so
 * nothing here knows what a contact is. A screen supplies its area, its metric
 * cards and how to render a row's primary cell; everything else comes from the
 * server's field catalogue.
 */
import { useEffect, useState, type ReactNode } from 'react'
import {
  ApiError, describeFilter, filterKey,
  type BulkResult, type FilterCondition, type ListCard,
} from '@rowted/shared'
import { PageBar, type PageTab } from '../frame/page-bar.js'
import {
  DataList, CellStack, type CardLine, type DataListCards, type DataListColumn, type MenuItem,
} from './data-list.js'
import { ListPagination } from './list-pagination.js'
import { ListEnd } from './list-end.js'
import { ScrollTopButton } from './scroll-top.js'
import { ListViewControls, ListTools, FilterChips, PhoneFilterStrip } from './list-controls.js'
import { BulkMenu, nounFor, scopeLine, type BulkAction, type BulkContext } from './bulk-menu.js'
import { SelectionSlot } from './selection-slot.js'
import { SelectionMenu } from './selection-menu.js'
import { PhoneSelectionBar } from './phone-selection-bar.js'
import { pageState } from '../lib/selection.js'
import { atMost, usePhone, useRung } from '../lib/use-narrow.js'
import { AdvancedFilter, filterLabels } from './list-filter.js'
import { CardStrip } from './card-strip.js'
import { CardBuilder } from './card-builder.js'
import { ConfirmDialog, Modal, TabRemove, Toast } from './overlays.js'
import { SmartImport } from './smart-import.js'
import { Button, Chip, EmptyState, Switch } from './primitives.js'
import { NAV_ICONS, IconClose, IconExport, IconFilter, IconPin, IconSearch } from '../frame/icons.js'
import { optionLabel } from './filter-kind-controls.js'
import { formatCell, formatNumber } from '../lib/format.js'
import { request as tenantRequest, type RequestOptions } from '../lib/api.js'
import { cn } from '../lib/cn.js'
import { useCards } from '../lib/use-cards.js'
import type { ListArea, ListRow, UseListResult } from '../lib/use-list.js'

export interface ListMetric {
  key: string
  label: string
  value: string
  sub?: string
  /** Drawn in the danger colour — "4 stalled > 5 days". */
  subUrgent?: boolean
  dotColor?: string
  /**
   * Plan §6.3: "metric cards are saved filters". Giving a card a filter makes
   * it selectable — clicking narrows the list to it, clicking again clears —
   * which is what makes the acceptance test ("selecting Completed · not
   * invoiced filters to status=Completed and invoiced_total=0") a property of
   * the card rather than of a handler written beside it.
   */
  filter?: FilterCondition[]
}

/**
 * Bulk actions, offered when rows are ticked.
 *
 * Only when they are ticked. The board used to raise the bar on a filter too,
 * reading "Completed · not invoiced" as a selection of twelve jobs — which is
 * what §6.3's mockup draws. In front of a person it does not work: filtering is
 * how you look at something, and a bar that offers to act on rows nobody chose,
 * with a count that moves as you keep filtering, reads as though the app has
 * made the selection for you.
 */
export interface ListBulk {
  /**
   * Declared, not drawn. The engine builds the menu, the pickers, the
   * confirm, the toast and the phone sheet from these (v3 §3.1).
   *
   * A list leaves out what this person may not do — the catalogue is built
   * with `me` in hand — and the server re-checks every call regardless.
   */
  actions: BulkAction[]
}

/**
 * What a `page` list's own page bar carries — the count and the saved views as
 * tabs — handed to a host that draws them in *its* page bar (`hostBar`).
 */
export interface ListHostBar {
  /** "8 of 27" — the page bar's sub. */
  sub: string
  /** All, then the saved views, each with what its × and menu do — exactly the page bar's tabs. */
  tabs: PageTab[]
  activeTab: string
  onTabChange: (key: string) => void
  /** "+ Save view": the engine's own dialog. */
  onSaveView: () => void
  /** The view on screen is not the saved one — the page bar's "Edited · not saved". */
  dirty: boolean
}

export interface ListScreenProps {
  area: ListArea
  title: string
  /**
   * Who owns the page.
   *
   * `page` is a list that *is* the screen — Contacts, Jobs, Properties — so it
   * draws the page bar and its saved views are the tabs in it.
   *
   * `embedded` is a list living inside somebody else's tab, like a catalogue
   * kind. The page bar belongs to the host, so the saved views become chips
   * and the New button sits below the tab rather than in the bar. Everything
   * under that — metrics, search, filters, the table, export, import — is the
   * same component either way, which is the point: one engine, so a change to
   * it lands on every list in Rowted at once.
   *
   * `related` is a list *about a record* — a property's jobs, a contact's
   * invoices. Same table, same headers, same sorting and dragging and pinning;
   * no search, no import, no export, no saved views, and every control rolled
   * into one button in the list's own header. Four rows tall, and it scrolls.
   */
  chrome?: 'page' | 'embedded' | 'related'
  list: UseListResult
  /** The bold primary line and its 11px muted second line (plan §3.5). */
  primaryColumn: {
    field: string
    header: string
    width?: number
    render: (row: ListRow) => { primary: ReactNode; secondary?: ReactNode }
  }
  /**
   * The card's second line on a phone: what this row **is**, where it sits,
   * who it belongs to (v3 §8, `docs/design-system.md` §6.1).
   *
   * A sentence the screen writes, rather than three columns the engine
   * happened to be showing — and it takes the place of both the generic facts
   * row and the primary column's secondary, because a card with all three is
   * a card with four lines on it. Every part must come from a field the area
   * fetches whatever the view's columns are (`alwaysFetch`, or `system`): a
   * row only carries what the list asked the server for, so a part taken from
   * a hidden column renders as a gap.
   *
   * Leave it out and the generic row stands, so no list has to supply one.
   */
  cardLine?: (row: ListRow) => CardLine | null
  metrics?: ListMetric[]
  /**
   * Whether this list carries the stat-card strip somebody can add to.
   *
   * On for a list that is a whole screen, off for one living inside another
   * screen's tab — a catalogue tab is a table of rates, and a dashboard over
   * it would be a dashboard nobody asked for in a place with no room for it.
   */
  cards?: boolean
  /**
   * The fetch the card strip uses.
   *
   * The *list* already carries its own — `useList` took it — but the strip is
   * built here, so it needs telling too. Rowted Admin's Clients screen passes
   * `adminRequest` to both (`docs/rowted-admin-v2.md` §2.8).
   */
  request?: <T>(path: string, options?: RequestOptions) => Promise<T>
  /**
   * A block between the metric cards and the search row.
   *
   * It exists for one screen: Invoicing's "Needs you today" queue, which is
   * not a list and not a metric — it is four things that will not fix
   * themselves, each with the button that fixes it. Putting it above the
   * toolbar rather than beside the list is what the mockup draws, and what
   * makes the screen answerable before anybody touches a filter.
   */
  beforeList?: ReactNode
  actions?: ReactNode
  onRowClick?: (row: ListRow) => void
  /** Per-column renderers, for columns that are more than formatted text. */
  cell?: Record<string, (row: ListRow) => ReactNode>
  /**
   * A last column of controls rather than data — the catalogue's Remove ×.
   *
   * Not a field, and deliberately not offered as one: the field catalogue is
   * what the database can sort, filter and export, and a button is none of
   * those. It is always last, has no header and takes no part in reordering.
   */
  rowActions?: { width?: number; render: (row: ListRow) => ReactNode }
  /** The 4px status colour at the row's left edge — the Jobs board's mark. */
  /**
   * The landing mark on the left of every row (v2 principle 1): a face, a map
   * square, a photo. Drawn in a pinned column, so it is still there when the
   * table scrolls sideways.
   */
  mark?: {
    width?: number
    render: (row: ListRow) => ReactNode
  }
  /** What the row's expand chevron opens. No chevron without it. */
  tray?: (row: ListRow) => ReactNode
  /** The row's ⋯ menu, led by the record's next action. */
  rowMenu?: (row: ListRow) => MenuItem[]
  /** Reads a group's heading. Defaults to the value the engine grouped on. */
  groupLabel?: (key: string, count: number) => string
  /**
   * Per-column width overrides in pixels, for columns the generic rule sizes
   * badly. A width the person set themselves lives on the view and wins.
   */
  columnWidths?: Record<string, number>
  bulk?: ListBulk
  /**
   * A link at the right of a `related` list's header — "View all", which opens
   * the real screen already narrowed to this record.
   */
  headerLink?: ReactNode
  emptyTitle: string
  emptyBody: string
  emptyAction?: { label: string; onClick: () => void }
  /** The org unit an import writes into. Required before Import is offered. */
  importOrgUnitId?: string
  /**
   * An `embedded` list whose host draws the count and the saved views in its
   * own page bar, as a `page` list's bar does — Activity's Tasks and All
   * activity on a phone (Aaron, Oct 27: "move the views up below the activity
   * title … so they are tabs there", "get rid of the line with list filter
   * chips, count and clear").
   *
   * Given, the engine hands it `ListHostBar` after every render (the host
   * keeps what changed, so its controls always call the list as it is now)
   * and draws none of what that replaces: the embedded section label and view
   * chips, and the phone's chip strip — whose count is now the host's sub and
   * whose conditions are the Filter button's, counted on its badge. The
   * dialogs a tab opens (Save view, Remove) stay the engine's. Absent, which
   * is every other list, nothing changes.
   */
  hostBar?: (bar: ListHostBar) => void
}

export function ListScreen({
  area, title, chrome = 'page', list, primaryColumn, cardLine, metrics, cards, beforeList, actions,
  onRowClick, cell,
  mark, tray, rowMenu, bulk, rowActions, groupLabel, columnWidths,
  headerLink, emptyTitle, emptyBody, emptyAction, importOrgUnitId, request, hostBar,
}: ListScreenProps) {
  /*
   * The fetch. Export builds its own request here rather than going through
   * `useList`, so it needs the same injection the hooks got — Rowted Admin's
   * Export must land on `/api/v1/platform/clients/export`, not on a tenant
   * route that would answer 401 (`docs/rowted-admin-v2.md` §2.8).
   */
  const send = request ?? tenantRequest
  const related = chrome === 'related'
  const [advancedOpen, setAdvancedOpen] = useState(false)
  // How much room the toolbar has, so it can fold rather than wrap.
  const { ref: toolbar, rung } = useRung()
  // Whether this is a phone at all, which is what decides table or cards.
  const phone = usePhone()
  /** The column a header's "Filter by…" handed to the filter popover. */
  const [filterField, setFilterField] = useState<string | null>(null)
  const [saving, setSaving] = useState(false)
  const [savingName, setSavingName] = useState('')
  const [savingShared, setSavingShared] = useState(false)
  const [removingView, setRemovingView] = useState<{ id: string; name: string } | null>(null)
  const [removingBusy, setRemovingBusy] = useState(false)
  const [updatingView, setUpdatingView] = useState<{ id: string; name: string } | null>(null)
  const [updatingBusy, setUpdatingBusy] = useState(false)
  const [renamingView, setRenamingView] = useState<{ id: string; name: string } | null>(null)
  const [renamedTo, setRenamedTo] = useState('')
  const [renamingBusy, setRenamingBusy] = useState(false)
  const [importOpen, setImportOpen] = useState(false)
  const [notice, setNotice] = useState<{ tone: 'default' | 'success' | 'danger'; text: string } | null>(null)

  /* The bulk action's three moments: asking, working, and what came of it. */
  const [confirming, setConfirming] = useState<
    { action: BulkAction; input?: unknown; restated?: string } | null>(null)
  const [bulkBusy, setBulkBusy] = useState(false)
  const [toast, setToast] = useState<
    { tone: 'success' | 'warning' | 'danger'; text: string
      action?: { label: string; onClick: () => void } } | null>(null)
  const [skipped, setSkipped] = useState<BulkResult | null>(null)
  /** The bulk sheet on a phone, raised from the selection bar. */
  const [phoneBulk, setPhoneBulk] = useState(false)

  /*
   * Esc clears the selection (§2.4) — unless the focus is in something you
   * type into, where Esc already means "never mind about this box" and
   * emptying a selection of fifty from under somebody's cursor would be the
   * least expected thing on the screen.
   */
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return
      const here = document.activeElement
      if (here instanceof HTMLInputElement || here instanceof HTMLTextAreaElement) return
      if (here instanceof HTMLElement && here.isContentEditable) return
      list.clearSelection()
    }
    window.addEventListener('keydown', onKey)
    return () => window.removeEventListener('keydown', onKey)
  }, [list])

  /*
   * The stat cards (v2 principle 4). A screen's own `metrics` come first and
   * are fixed — they are the numbers the module is *about*, written by the
   * screen — and everything after them is what this tenant's people added
   * themselves. Both filter the list the same way when clicked, so which is
   * which is a fact about who wrote it rather than about how it behaves.
   */
  const cardsOn = cards ?? chrome === 'page'
  /* The area's own glyph, when it has one — `NAV_ICONS` is keyed by area. */
  const AreaIcon = (NAV_ICONS as Partial<Record<string, typeof IconPin>>)[area]
  const cardStrip = useCards(area, cardsOn, send)
  const [builder, setBuilder] = useState<{ card: ListCard | null } | null>(null)

  /*
   * A card is "on" when the view's filter is exactly the card's. Comparing the
   * whole condition set rather than a stored card id means a filter a person
   * built by hand that happens to match a card lights that card up, which is
   * the honest answer to "what am I looking at?".
   */
  const isCardActive = (metric: ListMetric): boolean =>
    metric.filter !== undefined
    && filterKey(list.view.filter?.conditions ?? []) === filterKey(metric.filter)

  const toggleCard = (metric: ListMetric): void => {
    if (!metric.filter) return
    // Clicking the card you are already on clears it, so a card is a toggle
    // rather than a trap.
    list.setFilter(isCardActive(metric) ? [] : metric.filter)
  }

  /*
   * The selected rows that are **loaded**.
   *
   * Fewer than `list.selectedCount` whenever the selection reaches past the
   * page, which is the normal case now (§2.2) and always the case in
   * `matching` mode. An action's note judges on these and says so — "2 of 3
   * haven't been invited" is a statement about what is on screen — and the
   * server decides the rest.
   */
  /*
   * The row menu, plus **Select** on a phone (§7c).
   *
   * Holding a card already starts selecting, and a long-press is a gesture
   * nobody finds by looking. The ⋯ menu is where somebody looks, so the way
   * in is in it — on a phone only, because a desktop has the checkbox.
   */
  const rowMenuFor = (row: ListRow): MenuItem[] => {
    const own = rowMenu?.(row) ?? []
    if (!phone) return own
    return [
      { key: 'select', label: 'Select', onSelect: () => list.toggleSelected(row.id) },
      ...own,
    ]
  }

  const ticked = list.rows.filter((row) => list.isSelected(row.id))
  const selecting = list.selectedCount > 0

  const bulkCtx: BulkContext = {
    area,
    selection: list.selection,
    count: list.selectedCount,
    rows: ticked,
    view: list.view,
    search: list.search,
  }

  /*
   * Export and Deactivate belong to every list that can do them, so the
   * engine adds them rather than four pages each writing the same two rows in
   * a slightly different order (§3.1).
   */
  const bulkActions: BulkAction[] = [
    ...(bulk?.actions ?? []),
    {
      key: 'export',
      group: 'takeout' as const,
      label: (n: number) => `Export ${formatNumber(n)} as CSV`,
      icon: IconExport,
      local: () => exportView(),
    },
    ...(list.archivable
      ? [{
        key: 'deactivate',
        group: 'takeout' as const,
        danger: true,
        label: (n: number) => `Deactivate ${formatNumber(n)}…`,
        icon: IconClose,
        confirm: (ctx: BulkContext) => ({
          title: `Deactivate ${formatNumber(ctx.count)} ${nounFor(ctx)}?`,
          body: <DeactivateBody ctx={ctx} list={list} />,
          confirmLabel: `Deactivate ${formatNumber(ctx.count)}`,
        }),
      }]
      : []),
  ]

  /** Which way a column is sorted right now, for the arrow in its header. */
  const sortOf = (field: string): 'asc' | 'desc' | null =>
    list.view.sort.find((spec) => spec.field === field)?.direction ?? null

  /** `sum:propertyCount` — how runList keys a total. */
  const aggregateKey = (field: string): string | null => {
    const spec = list.view.aggregates.find((entry) => entry.field === field)
    return spec ? `${spec.fn}:${spec.field}` : null
  }

  /**
   * The column's own menu — v2's "a power user never leaves the header".
   *
   * Everything here is something the toolbar can also do. That is the point:
   * the toolbar is for finding out that it is possible, and this is for doing
   * it without crossing the screen. Filter by opens the step filter with the
   * field already chosen, which is the one that saves the most.
   */
  const headerMenu = (key: string): MenuItem[] => {
    const field = list.fields.find((candidate) => candidate.name === key)
    if (!field) return []

    const items: MenuItem[] = []

    if (field.sortable) {
      items.push(
        { key: 'asc', label: 'Sort A → Z', onSelect: () => list.setSort([{ field: key, direction: 'asc' }]) },
        { key: 'desc', label: 'Sort Z → A', onSelect: () => list.setSort([{ field: key, direction: 'desc' }]) },
      )
    }
    if (field.filterable) {
      items.push({
        key: 'filter',
        label: `Filter by ${field.label}…`,
        hint: '⌘F',
        separatorBefore: items.length > 0,
        /*
         * Opens the filter with step 1 answered, rather than applying
         * anything. It used to add `is ""` straight to the view, which matches
         * no row and emptied the whole table — from a menu item whose name and
         * trailing ellipsis both promise to open something.
         */
        onSelect: () => setFilterField(key),
      })
    }
    if (field.groupable) {
      items.push({
        key: 'group',
        label: `Group by ${field.label}`,
        onSelect: () => list.setGroup(list.view.group === key ? undefined : key),
      })
    }
    if (field.aggregatable) {
      const current = list.view.aggregates.find((spec) => spec.field === key)
      items.push({
        key: 'sum',
        label: current ? `Summarize · ${current.fn} (off)` : 'Summarize · sum',
        onSelect: () => list.setAggregate(key, current ? null : 'sum'),
      })
    }

    const isPinned = (list.view.pinned ?? []).includes(key)
    items.push(
      {
        key: 'pin',
        label: isPinned ? 'Unpin column' : 'Pin column left',
        separatorBefore: true,
        onSelect: () => list.togglePin(key),
      },
      { key: 'hide', label: 'Hide column', onSelect: () => list.toggleField(key) },
    )

    return items
  }

  /*
   * Every column comes from the field picker, in the view's own order.
   *
   * The primary column used to be prepended outside that order, which made it
   * the one column nobody could move, hide or put second — "you lock the first
   * column on each list". It is now an ordinary column that happens to render
   * as a bold line over a muted one, so where it sits is the person's business.
   */
  const columns: DataListColumn<ListRow>[] = list.visibleFields
    .filter((field) => field.name !== 'id')
    .map((field): DataListColumn<ListRow> => {
      if (field.name === primaryColumn.field) {
        return {
          key: field.name,
          header: primaryColumn.header,
          icon: field.icon,
          width: list.view.columnWidths?.[field.name] ?? primaryColumn.width ?? 230,
          sort: field.sortable ? sortOf(field.name) : undefined,
          render: (row) => {
            const { primary, secondary } = primaryColumn.render(row)
            /*
             * The name, and the one line under it that says which record
             * this is — a phone number and an address, a job id and a
             * property. Both on a card and both in the table, because they
             * are the same two facts and a screen with two answers to "what
             * goes under the name" is a screen that drifts.
             *
             * It used to be the name alone whenever the screen supplied a
             * `cardLine`, on the reasoning that the line already said what
             * the row was. That reasoning was wrong about which line: the
             * third row carries the *state* of a record — its chip, its
             * branch, when it last moved — and the second carries its
             * *identity*. Dropping the second left a card whose middle said
             * nothing, so the name had to carry the whole job of telling two
             * Smiths apart.
             */
            return <CellStack primary={primary} secondary={secondary} />
          },
        }
      }

      return {
        key: field.name,
        header: field.label,
        numeric: field.numeric,
        // `undefined` means the header is not a sort control at all, which is
        // what a column the server refuses to sort on has to be.
        sort: field.sortable ? sortOf(field.name) : undefined,
        icon: field.icon,
        /*
         * The screen's suggestion only. A width the person dragged goes to the
         * table as `userWidths`, because the two are not the same kind of
         * thing: the fit is allowed to overrule a suggestion and is not
         * allowed to overrule a person.
         */
        width: columnWidths?.[field.name],
        render: (row) => cell?.[field.name]?.(row)
          ?? formatCell(row[field.name], field.kind, field.options),
        // The footer and group rows show what the Σ menu asked for, computed by
        // the server — the client never re-totals a page it only partly has.
        // The engine keys a total by function and field, not field alone, so
        // sum and avg of one column can both be asked for.
        aggregate: aggregateKey(field.name)
          ? ({ groupKey }) => formatCell(
            groupKey === null
              ? list.totals[aggregateKey(field.name)!]
              // The server totals each group as well as the whole list; a
              // client that re-totalled would only ever total this page.
              : list.groups.find((group) => group.key === groupKey)
                ?.aggregates[aggregateKey(field.name)!],
            field.kind)
          : undefined,
      }
    })

  if (rowActions) {
    columns.push({
      key: '__actions', header: '', width: rowActions.width ?? 44, render: rowActions.render,
    })
  }

  /*
   * What a row says on a phone — plan §3.7, and the same decision for every
   * list in Rowted because there is only one of these.
   *
   * Three lines, and deliberately not more. The name and the muted line under
   * it are the primary column doing what it already does. The third line is
   * how the record stands and when: a status chip, one money figure if the
   * area has one, and a date pushed to the right. A fourth value would be a
   * bare number with no header over it, which on a 358px card is a riddle
   * rather than information — the column headings that explain those numbers
   * are exactly what a card does not have.
   *
   * Derived from the *visible* fields, in the order the person put them, so
   * hiding a column on a phone hides it from the card too. Nothing about a
   * particular screen is written here: Jobs gets its status and value because
   * Jobs has a status field and a money field, not because anybody special
   * cased it.
   */
  const cardLayout = ((): DataListCards<ListRow> => {
    const mine = (field: { name: string }) =>
      field.name !== primaryColumn.field && field.name !== 'id'
    const visible = list.visibleFields.filter(mine)

    const chip = visible.find((field) => field.name === 'status')
      ?? visible.find((field) => field.kind === 'enum')
    const money = visible.find((field) => field.kind === 'money')

    /*
     * The date, and only one the view is actually showing.
     *
     * A card cannot fall back to a column the list did not ask the server
     * for: a list request carries the view's fields and the rows come back
     * with those and nothing else, so a "clever" default would render an
     * empty slot rather than a date. Where an area has no date among its
     * columns — the Jobs board, whose seven are all status and money — the
     * third row is the chip and the figure, and the card is no worse for it.
     *
     * A deadline beats a milestone when the view has both, which is why the
     * name is looked at: a visit's scheduled date and an invoice's due date
     * are what somebody standing in a yard needs, and "created" is not.
     */
    const dated = (field: { kind: string }) =>
      field.kind === 'date' || field.kind === 'datetime'
    const trailing = visible.find(
      (field) => dated(field) && /due|scheduled|expires/i.test(field.name))
      ?? visible.find(dated)

    return {
      title: primaryColumn.field,
      chip: chip?.name,
      meta: money ? [money.name] : [],
      trailing: trailing?.name,
      ...(cardLine ? { line: cardLine } : {}),
    }
  })()

  // With no grouping the whole list is one unlabelled group, which is what the
  // DataList already draws for a flat table.
  const groupedField = list.fields.find((field) => field.name === list.view.group)
  const groups = list.view.group && list.groups.length > 0
    ? list.groups.map((group) => ({
      key: group.key,
      /*
       * The field's own name for the value, where it has one — "PA ·
       * Pennsylvania · 1,302" rather than "PA · 1,302". The header has room
       * for the sentence and the column does not, which is the whole reason
       * `options` is separate from what is stored.
       */
      label: groupLabel
        ? groupLabel(group.label, group.count)
        : `${optionLabel(groupedField, group.key) || group.label} · ${formatNumber(group.count)}`,
      rows: list.rows.filter((row) => String(row[list.view.group!] ?? '') === group.key),
      // The group's real size, which is not the number of its rows on this page.
      total: group.count,
    }))
      /*
       * Every group the filter matches comes back, because the header count
       * and the rollup are about the whole list. But a group whose rows are
       * all on a later page has nothing to draw, and a screen of bare headers
       * reads as a list that failed to load. The ones with rows keep their
       * real totals, so "Scheduled · 320 · showing 50 of 320" still says what
       * is behind the page.
       */
      .filter((group) => group.rows.length > 0)
    : [{ key: 'all', label: '', rows: list.rows }]

  async function exportView() {
    try {
      const result = await send<{ csv: string; rowCount: number }>(`/${area}/export`, {
        method: 'POST',
        body: { view: list.view, search: list.search || undefined },
      })
      download(`${area}-${new Date().toISOString().slice(0, 10)}.csv`, result.csv)
      setNotice({ tone: 'success', text: `Exported ${formatNumber(result.rowCount)} rows.` })
    } catch (cause) {
      setNotice({ tone: 'danger', text: cause instanceof Error ? cause.message : 'Export failed.' })
    }
  }

  async function removeView(saved: { id: string; name: string }) {
    setRemovingBusy(true)
    try {
      await list.deleteView(saved.id)
      setNotice({ tone: 'success', text: `Removed “${saved.name}”.` })
      setRemovingView(null)
    } catch (cause) {
      setNotice({
        tone: 'danger',
        text: cause instanceof Error ? cause.message : 'That view could not be removed.',
      })
    } finally {
      setRemovingBusy(false)
    }
  }

  async function saveOverView(saved: { id: string; name: string }) {
    setUpdatingBusy(true)
    try {
      await list.updateView(saved.id)
      setNotice({ tone: 'success', text: `Updated “${saved.name}”.` })
      setUpdatingView(null)
    } catch (cause) {
      setNotice({
        tone: 'danger',
        text: cause instanceof Error ? cause.message : 'That view could not be updated.',
      })
    } finally {
      setUpdatingBusy(false)
    }
  }

  async function renameSavedView(saved: { id: string; name: string }, name: string) {
    setRenamingBusy(true)
    try {
      await list.renameView(saved.id, name)
      setNotice({ tone: 'success', text: `Renamed to “${name}”.` })
      setRenamingView(null)
    } catch (cause) {
      setNotice({
        tone: 'danger',
        text: cause instanceof Error ? cause.message : 'That view could not be renamed.',
      })
    } finally {
      setRenamingBusy(false)
    }
  }

  const openSaveView = () => { setSavingName(''); setSaving(true) }

  /*
   * The saved views as the page bar's tabs — the `page` list's own bar, or a
   * host's (`hostBar`), one list so the two cannot differ.
   */
  const viewTabs: PageTab[] = [
    // "All" is the area itself, not something anybody made, so it has
    // no ×.
    { key: '', label: 'All' },
    ...list.savedViews.map((saved) => ({
      key: saved.id,
      label: saved.name,
      isDefault: saved.isDefault,
      onToggleDefault: () => {
        void list.setDefaultView(saved.isDefault ? null : saved.id)
      },
      onUpdate: () => setUpdatingView(saved),
      onRename: () => { setRenamedTo(saved.name); setRenamingView(saved) },
      onRemove: () => setRemovingView(saved),
    })),
  ]
  const barSub = `${formatNumber(list.visibleCount)} of ${formatNumber(list.totalCount)}`
  const openTab = (key: string) => list.openSavedView(key || null)

  /*
   * After every render, so the controls the host draws always call this
   * render's list; the host keeps only what changed (`hostBar`).
   */
  useEffect(() => {
    hostBar?.({
      sub: barSub, tabs: viewTabs, activeTab: list.activeViewId ?? '', onTabChange: openTab,
      onSaveView: openSaveView, dirty: list.dirty,
    })
  })

  /**
   * Dropping a column on another one.
   *
   * The table's column list has the primary column pinned at the front and
   * computed columns spliced in, and neither of those is a field — so the
   * index the header hands over is a position among *columns*, and what
   * `moveField` wants is a position among the view's fields. Translating
   * between them here is why the primary column can sit anywhere and a
   * computed column can sit in the middle without the drag landing in the
   * wrong place.
   */
  const reorder = (key: string, toIndex: number) => {
    const names = columns.map((column) => column.key)
    const target = names[toIndex]
    const shown = list.visibleFields.map((field) => field.name)
    const at = target ? shown.indexOf(target) : -1
    if (at < 0) return
    list.moveField(key, at)
  }

  /**
   * Choosing a bulk action: picker, confirm, run, tell the truth about it.
   *
   * The confirm is not decoration on the `matching` branch. "Deactivate 126"
   * over a selection the person cannot see every row of is the one place this
   * feature can do real damage, so the body spells out the filter it is
   * acting through, says how many were left out, and says in as many words
   * that it is not just the fifty on screen.
   */
  async function runAction(action: BulkAction, input?: unknown) {
    if (action.local) { await action.local(bulkCtx); return }

    const needsConfirm = action.danger
      || (list.selection.mode === 'matching' && list.selectedCount > 50)
      || action.confirm?.(bulkCtx, input) !== undefined
    if (needsConfirm && !confirming) {
      setConfirming({ action, input })
      return
    }

    setConfirming(null)
    setBulkBusy(true)
    try {
      const result = await list.runBulk(action.key, input)
      list.clearSelection()
      list.reload()

      if (result.changed === 0) {
        setToast({
          tone: 'danger',
          text: `Nothing changed${result.skipped[0] ? ` — ${result.skipped[0].reason}` : '.'}`,
        })
      } else if (result.skipped.length > 0) {
        setToast({
          tone: 'warning',
          text: `${action.label(result.changed)} · ${formatNumber(result.skipped.length)} skipped`,
          action: { label: 'See why', onClick: () => setSkipped(result) },
        })
      } else {
        setToast({
          tone: 'success',
          text: action.label(result.changed),
          ...(result.undoable
            ? { action: { label: 'Undo', onClick: () => void undoBatch(action, result.batchId) } }
            : {}),
        })
      }
    } catch (cause) {
      /*
       * `selection_changed` is not an error to apologise for — it is the
       * guard working. Somebody added a matching row between the confirm and
       * the press, so the confirm re-opens carrying the new number rather
       * than the action quietly running on 127 after the button said 126.
       */
      const code = cause instanceof ApiError ? cause.code : null
      if (code === 'selection_changed') {
        setConfirming({ action, input, restated: cause instanceof ApiError ? cause.message : '' })
      } else {
        setToast({
          tone: 'danger',
          text: cause instanceof Error ? cause.message : 'That could not be done.',
        })
      }
    } finally {
      setBulkBusy(false)
    }
  }

  /** Undo, which is only ever offered where an exact opposite exists (§3.5). */
  async function undoBatch(action: BulkAction, batchId: string) {
    try {
      await send(`/${area}/bulk-actions/${UNDO_OF[action.key] ?? action.key}`, {
        method: 'POST',
        body: { selector: { mode: 'batch', batchId } },
      })
      list.reload()
      setToast({ tone: 'success', text: 'Put back.' })
    } catch (cause) {
      setToast({
        tone: 'danger',
        text: cause instanceof Error ? cause.message : 'That could not be undone.',
      })
    }
  }

  return (
    <>
      {chrome === 'page' && (
        <PageBar
          title={title}
          sub={barSub}
          /*
           * No actions. v2 moves Import, Export and the New button down to the
           * toolbar, leaving the page bar as title, count and the saved views
           * — which is the row that says where you are, not what you can do.
           */
          tabs={viewTabs}
          activeTab={list.activeViewId ?? ''}
          onTabChange={openTab}
          onSaveView={openSaveView}
          status={list.dirty
            ? <span className="text-[12px] text-text-muted">Edited · not saved</span>
            : undefined}
        />
      )}

      {related && (
        /*
          * A related list is a card on somebody's record page: its own header
          * with the name, the count and the one button, then four rows of
          * table that scroll. No toolbar — see `chrome` above for why.
          */
        <div className="min-w-0 overflow-hidden rounded-card border border-line bg-surface">
          <header className="flex h-10 items-center gap-2 border-b border-line px-3">
            <span className="text-card text-text">{title}</span>
            <span className="tnum text-sub text-text-muted">
              {formatNumber(list.visibleCount)}
            </span>
            <span className="ml-auto flex items-center gap-2">
              {headerLink}
              <ListTools
                list={list}
                related
                onAdvanced={() => setAdvancedOpen(true)}
              />
            </span>
          </header>

          {list.error
            ? (
              <EmptyState
                title="This list could not load"
                body={list.error}
                action={{ label: 'Try again', onClick: list.reload }}
              />
            )
            : (
              /*
                * Four rows, then it scrolls — and the ceiling is the table's,
                * not a wrapper's. A wrapper that scrolled vertically left the
                * header sticking to the wrong thing, so the column headings
                * scrolled away with the rows; and it let a wide table widen
                * the page instead of scrolling inside the card.
                */
              <DataList
                columns={columns}
                groups={groups}
                rowKey={(row) => row.id}
                mark={mark}
                tray={tray}
                rowMenu={rowMenuFor}
                cards={phone ? cardLayout : undefined}
                pinned={list.view.pinned ?? []}
                density={list.view.density}
                maxHeight={phone ? undefined : relatedHeight(list.view.density)}
                loading={list.loading && list.rows.length === 0}
                onRowClick={onRowClick}
                onSort={(key, additive) => list.toggleSort(key, additive)}
                onReorder={reorder}
                onResize={list.setColumnWidth}
                userWidths={list.view.columnWidths}
                headerMenu={headerMenu}
                showFooter={list.view.aggregates.length > 0}
                emptyState={<EmptyState title={emptyTitle} body={emptyBody} />}
              />
            )}
        </div>
      )}

      {!related && (
      <div className={cn('flex flex-col gap-3', chrome === 'page' && 'p-4',
        /* Room under the last card for the bar fixed over it (§7c). */
        phone && selecting && 'pb-[88px]')}>
        {/*
          * What this tab is, and how many — `phone-list-loading.md` rule 1.
          *
          * A phone list has no pager, so the count it used to carry has to be
          * somewhere. On a list that is the page it is the page bar's sub; on
          * a tab it is here, in the section label the mockup draws above the
          * cards, in the same words the tab bar's pill uses.
          */}
        {chrome === 'embedded' && phone && !hostBar && (
          <div className={cn('flex items-center gap-2 px-0 text-[12px] font-semibold uppercase',
            'tracking-[0.05em] text-text-faint')}>
            {AreaIcon && <span className="flex shrink-0 text-accent"><AreaIcon size={14} /></span>}
            {title}
            <span className="tnum font-semibold normal-case tracking-normal text-text-faint">
              · {formatNumber(list.visibleCount)}
            </span>
          </div>
        )}

        {/* A host that draws the views as its page bar's tabs (`hostBar`) draws these instead. */}
        {chrome === 'embedded' && !hostBar && (
          <div className="flex flex-wrap items-center gap-1.5">
            <Chip active={!list.activeViewId} onClick={() => list.openSavedView(null)}>
              All
            </Chip>
            {list.savedViews.map((saved) => (
              <Chip
                key={saved.id}
                active={saved.id === list.activeViewId}
                onClick={() => list.openSavedView(saved.id)}
              >
                {saved.isDefault && (
                  <span
                    aria-label="Your default view"
                    title="Your default view"
                    className="flex shrink-0 text-accent"
                  >
                    <IconPin size={11} />
                  </span>
                )}
                {saved.name}
                <TabRemove label={saved.name} onRemove={() => setRemovingView(saved)} />
              </Chip>
            ))}
            <button
              type="button"
              onClick={openSaveView}
              className="px-1.5 text-cell text-accent hover:underline"
            >
              + Save view
            </button>
            {list.dirty && (
              <span className="text-sub text-text-muted">Edited · not saved</span>
            )}
            {/*
              * The count and the green button live on the toolbar, which is
              * the row below this one and is the same row on every list in the
              * product. Putting them here too drew both twice — two New job
              * buttons a thumb apart, and two "2 of 5"s.
              */}
          </div>
        )}

        {/*
          * One row, and only ever one. The strip measures itself and gives the
          * cards up a piece at a time rather than wrapping — a second row of
          * cards pushes the table below the fold, and the table is the page.
          * The screen's fixed metrics go through it too, so the row degrades
          * as one thing.
          */}
        {((metrics && metrics.length > 0) || cardsOn) && (
          <CardStrip
            list={list}
            listTitle={title}
            cards={cardStrip}
            cardsOn={cardsOn}
            metrics={metrics}
            metricSelected={(metric) => (metric.filter ? isCardActive(metric) : false)}
            onMetricClick={(metric) => { if (metric.filter) toggleCard(metric) }}
            onEdit={(card) => setBuilder({ card })}
          />
        )}

        {beforeList}

        {/*
          * Search first, then everything that changes what you are looking at,
          * then a gap, then the tools. The view controls sat at the far right
          * with the tools, which put the two most-used buttons on the screen a
          * whole page away from the box people had just typed in.
          */}
        {/*
          * One row, on every width.
          *
          * It used to wrap, and a wrapped toolbar is not a smaller toolbar —
          * it is a second row that pushes the table down and moves every
          * control to somewhere nobody expects. So it folds instead, in the
          * order the things on it are worth: the buttons lose their words,
          * then Import and Export fold into the settings menu, then the count
          * goes and the search box gives up its minimum. What is left at phone
          * width is a search box, Filter, the settings gear and the one green
          * button — which is the whole job.
          *
          * Measured off this element rather than a media query, because the
          * rail is 224px wide when it is out and 60px when it is not, and the
          * window knows nothing about that.
          */}
        <div ref={toolbar} className="flex min-w-0 flex-nowrap items-center gap-2">
          <label
            className={cn(
              'relative flex h-9 shrink items-center',
              atMost(rung, 'phone') ? 'min-w-[96px] flex-1' : 'min-w-[240px]',
            )}
          >
            <span className="sr-only">Search {title.toLowerCase()}</span>
            <span className="pointer-events-none absolute left-2 text-text-muted">
              <IconSearch size={15} />
            </span>
            <input
              value={list.search}
              onChange={(event) => list.setSearch(event.target.value)}
              placeholder="Search…"
              className={cn(
                'h-9 w-full rounded-field border border-line bg-surface pl-7 pr-2',
                'text-cell text-text placeholder:text-text-muted',
              )}
            />
          </label>

          <ListViewControls
            list={list}
            wordless={atMost(rung, 'icons')}
            startField={filterField}
            onOpenChange={(open) => { if (!open) setFilterField(null) }}
            onAdvanced={() => { setFilterField(null); setAdvancedOpen(true) }}
          />

          {/*
            * Not on a phone, where they go on their own line below (§7b).
            *
            * At 390px this row is a search box, Filter, the gear and the
            * green button, and all four of those are there to be pressed.
            * The chips are an answer to "what am I looking at", and putting
            * an answer in the row of controls is what made the New button
            * leave the right-hand edge of the screen: `FilterChips` is
            * `flex-1`, so with three conditions on it the sentence and the
            * Clear button took whatever the search box would give up and
            * then pushed everything after them sideways.
            */}
          {!phone && <FilterChips list={list} onOpenField={setFilterField} />}

          {/*
            * The count, and what replaces it while something is ticked (§2.4).
            *
            * Same slot, same row, so the table's top edge never moves — which
            * is the whole of why the old bar above the table is gone.
            *
            * **Never on a phone**, selecting or not (§7a: "row one never
            * changes"). It used to appear there while something was ticked,
            * and it cost twice: it said "1 selected · Clear" in the same
            * breath as `PhoneSelectionBar` pinned to the bottom of the
            * screen, and it took the width out of the search box to do it. A
            * phone has one surface for a selection and it is the bar.
            */}
          {!phone && (!atMost(rung, 'folded') || selecting) && (
            <SelectionSlot
              selection={list.selection}
              count={list.selectedCount}
              visibleCount={list.visibleCount}
              totalCount={list.totalCount}
              onSelectAll={list.selectAllMatching}
              onClear={list.clearSelection}
              tight={atMost(rung, 'folded')}
            />
          )}

          {/*
            * The last rung: the primary action keeps its glyph and loses its
            * words. "New contact" is 116px of a 298px bar, and a bar that
            * cannot fit its own buttons pushes the page sideways — which is
            * the failure this whole row is here to avoid. The label stays in
            * `aria-label` and in the tooltip, so nothing is lost but the ink.
            */}
          <div className="ml-auto flex shrink-0 items-center gap-2">
            {/*
              * Bulk actions, on every list that can do anything at all — and
              * greyed rather than absent when nothing is ticked, because a
              * control that appears only after you have done the thing it is
              * for is a control nobody finds (§3.2).
              */}
            {!phone && !related && bulkActions.length > 0 && (
              <BulkMenu
                list={list}
                actions={bulkActions}
                ctx={bulkCtx}
                onChoose={(action, input) => void runAction(action, input)}
                wordless={atMost(rung, 'folded')}
              />
            )}
            <ListTools
              list={list}
              onExport={exportView}
              onImport={importOrgUnitId ? () => setImportOpen(true) : undefined}
              onNew={actions}
              squash={atMost(rung, 'phone')}
            />
          </div>
        </div>

        {/*
          * The chips, on a phone, on a line of their own — and only while
          * there are any (§7b).
          *
          * The count comes with them, because the count is the thing the
          * filter changed and the toolbar above has no room for it at this
          * width. The strip scrolls sideways and the page does not: `-mx-4
          * px-4` lets a chip run to the edge of the screen rather than
          * stopping at the page's gutter, which is what makes it read as
          * scrollable, and `Clear` is pinned to the right over a fade so
          * there is always a way out of a filter whose chips run off the
          * side.
          */}
        {/* Not under a host's bar, which says the count; the Filter button's badge counts the rest (`hostBar`). */}
        {phone && !hostBar && <PhoneFilterStrip list={list} onOpenField={setFilterField} />}

        {/*
          * Nothing goes here any more (§2.4).
          *
          * A bar used to slide in above the table when a row was ticked, and
          * it pushed the table down by forty pixels — so the row somebody was
          * about to tick next was no longer under their pointer, and ticking
          * three rows cost three corrections. The selection lives in the
          * toolbar row instead, in the slot the count was already in, and the
          * table's top edge is identical in every state.
          */}

        {/*
          * One framed card around the table and its pagination.
          *
          * The list is a thing on the page, like every card above it — without
          * the border it bled into the background and the rounded corners the
          * rest of the app has stopped at the toolbar. `overflow-hidden` is
          * what makes the corners actually cut the header and footer rows.
          */}
        {/*
          * One framed card around the table — except on a phone, where the
          * rows are cards of their own and a frame around a stack of cards is
          * a box around a box.
          */}
        <div className={cn(phone
          ? 'min-w-0'
          : 'overflow-hidden rounded-card border border-line bg-surface')}>
          {list.error
            ? (
              <EmptyState
                title="This list could not load"
                body={list.error}
                action={{ label: 'Try again', onClick: list.reload }}
              />
            )
            : (
            <DataList
              columns={columns}
              groups={groups}
              rowKey={(row) => row.id}
              mark={mark}
              tray={tray}
              rowMenu={rowMenuFor}
              cards={phone ? cardLayout : undefined}
              pinned={list.view.pinned ?? []}
              density={list.view.density}
              loading={list.loading && list.rows.length === 0}
              onRowClick={onRowClick}
              selection={{
                isSelected: list.isSelected,
                anySelected: selecting,
                onToggle: list.toggleSelected,
                onToggleRange: list.toggleRange,
                header: {
                  ...pageState(list.selection, list.rows.map((row) => row.id)),
                  onToggle: list.togglePage,
                  menu: (
                    <SelectionMenu
                      selection={list.selection}
                      pageSize={list.rows.length}
                      visibleCount={list.visibleCount}
                      pageChecked={pageState(
                        list.selection, list.rows.map((row) => row.id)).checked}
                      onSelectPage={list.selectPage}
                      onSelectAll={list.selectAllMatching}
                      onClear={list.clearSelection}
                    />
                  ),
                },
              }}
              onSort={(key, additive) => list.toggleSort(key, additive)}
              onReorder={reorder}
              onResize={list.setColumnWidth}
              userWidths={list.view.columnWidths}
              headerMenu={headerMenu}
              showFooter={list.view.aggregates.length > 0 || columns.some((c) => c.footer)}
              emptyState={
                <EmptyState title={emptyTitle} body={emptyBody} action={emptyAction} />
              }
            />
            )}

          {/*
            * Rule 6 of `docs/phone-list-loading.md`: a list whose rows fit in
            * one page shows no pager on any width. "1–30 of 30" beside two
            * arrows that cannot be pressed is a control describing itself.
            */}
          {!list.error && list.visibleCount > 0 && !phone && list.pageCount > 1 && (
            <ListPagination
              page={list.page}
              pageCount={list.pageCount}
              pageSize={list.pageSize}
              total={list.visibleCount}
              shown={list.rows.length}
              onPage={list.setPage}
              onPageSize={list.setPageSize}
            />
          )}

          {/*
            * A phone list has no pager: it loads as you scroll and ends by
            * saying so (`docs/phone-list-loading.md`). The count somebody
            * would have read off a pager is in the page header or the record
            * tab's section label, where it was already.
            */}
          {phone && !list.error && list.rows.length > 0 && (
            <ListEnd
              total={list.visibleCount}
              shown={list.rows.length}
              hasMore={list.hasMore}
              loadingMore={list.loadingMore}
              failed={list.moreFailed}
              onMore={list.loadMore}
              noun={title.toLowerCase()}
              pageSize={list.pageSize}
            />
          )}
        </div>

        {/*
          * Selection is the only bar a phone list keeps.
          *
          * It is a mode — you are acting on rows rather than moving through
          * them — so while anything is ticked the bulk actions sit under the
          * thumb that ticked them. It used to alternate with a compact pager;
          * there is no pager now, so it replaces nothing and appears only
          * when there is something to act on.
          *
          * It is `fixed` to the bottom of the screen rather than sticky at
          * the end of the page, and the column above adds `pb-[88px]` while
          * it is up so the last card clears it.
          */}
        {phone && !list.error && selecting && (
          <PhoneSelectionBar
            selection={list.selection}
            count={list.selectedCount}
            visibleCount={list.visibleCount}
            onSelectAll={list.selectAllMatching}
            onClear={list.clearSelection}
            onBulk={() => setPhoneBulk(true)}
            bottom={chrome === 'page' ? 0 : 58}
          />
        )}

        {/*
          * Back to the top, over whatever the bottom edge already holds: the
          * home indicator on a list that is the page, the record page's 58px
          * tab bar on one that is a tab.
          */}
        {phone && !list.error && (
          /* Above the selection bar when there is one, not behind it. */
          <ScrollTopButton
            bottom={(chrome === 'page' ? 12 : 58 + 12) + (selecting ? SELECTION_BAR : 0)}
          />
        )}
      </div>
      )}

      <AdvancedFilter
        list={list}
        open={advancedOpen}
        onClose={() => setAdvancedOpen(false)}
        /*
          * Applied first, then named. The save dialog saves the view that is
          * on screen, so handing it a filter the list is not wearing would
          * save something nobody has seen — and the tab it creates would open
          * on a table that looks nothing like the builder's count said.
          */
        onSaveView={(filter) => {
          list.setFilter(filter.conditions)
          setAdvancedOpen(false)
          openSaveView()
        }}
      />

      {builder && (
        <CardBuilder
          list={list}
          listTitle={title}
          cards={cardStrip}
          editing={builder.card}
          onClose={() => setBuilder(null)}
        />
      )}

      {/* --- rename a view --- */}
      <Modal
        open={renamingView !== null}
        onClose={() => setRenamingView(null)}
        title={renamingView ? `Rename “${renamingView.name}”` : 'Rename view'}
        footer={
          <>
            <Button onClick={() => setRenamingView(null)} disabled={renamingBusy}>Cancel</Button>
            <Button
              variant="primary"
              disabled={renamingBusy || !renamedTo.trim() || renamedTo.trim() === renamingView?.name}
              onClick={() => {
                if (renamingView) void renameSavedView(renamingView, renamedTo.trim())
              }}
            >
              {renamingBusy ? 'Renaming…' : 'Rename'}
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1 block text-cell text-text-muted">Name</span>
          <input
            autoFocus
            value={renamedTo}
            onChange={(event) => setRenamedTo(event.target.value)}
            onKeyDown={(event) => {
              if (event.key !== 'Enter' || !renamedTo.trim() || !renamingView) return
              event.preventDefault()
              void renameSavedView(renamingView, renamedTo.trim())
            }}
            className="h-9 w-full rounded-field border border-line bg-surface px-2 text-cell text-text"
          />
        </label>
        <p className="mt-2 text-sub text-text-muted">
          Only the name changes. What the view shows stays as it is.
        </p>
      </Modal>

      {/* --- save view --- */}
      <Modal
        open={saving}
        onClose={() => setSaving(false)}
        title="Save this view"
        footer={
          <>
            <Button onClick={() => setSaving(false)}>Cancel</Button>
            <Button
              variant="primary"
              disabled={!savingName.trim()}
              onClick={async () => {
                await list.saveView(savingName.trim(), savingShared)
                setSaving(false)
                setNotice({ tone: 'success', text: 'View saved.' })
              }}
            >
              Save view
            </Button>
          </>
        }
      >
        <label className="block">
          <span className="mb-1 block text-cell text-text-muted">Name</span>
          <input
            value={savingName}
            onChange={(event) => setSavingName(event.target.value)}
            placeholder="Needs a phone number"
            className="h-9 w-full rounded-field border border-line bg-surface px-2 text-cell text-text"
          />
        </label>
        <div className="mt-3 flex items-center gap-2.5">
          <Switch
            checked={savingShared}
            onChange={setSavingShared}
            label="Share with everyone who can see this list"
            hideLabel
          />
          <span className="text-cell text-text">Share with everyone who can see this list</span>
        </div>
      </Modal>

      {importOpen && importOrgUnitId && (
        <SmartImport
          area={area}
          orgUnitId={importOrgUnitId}
          /*
            * An id column is how a row says which record it updates, so it
            * stays in the list somebody maps onto — it is the one "system"
            * field an import genuinely needs.
            */
          fields={list.fields.map((field) => ({ name: field.name, label: field.label }))}
          onClose={() => setImportOpen(false)}
          onDone={(summary: string) => {
            setImportOpen(false)
            list.reload()
            setNotice({ tone: 'success', text: summary })
          }}
        />
      )}

      {removingView && (

        <ConfirmDialog

          title={`Remove “${removingView.name}”?`}

          busy={removingBusy}

          body={

            <p className="m-0 text-body text-text-muted">

              The view goes; the records it filtered stay exactly where they are.

              {' '}Anybody you shared it with loses it too.

            </p>

          }

          onCancel={() => setRemovingView(null)}

          onConfirm={() => void removeView(removingView)}

        />

      )}


      {updatingView && (
        <ConfirmDialog
          title={`Update “${updatingView.name}”?`}
          confirmLabel="Update view"
          busyLabel="Updating…"
          tone="default"
          busy={updatingBusy}
          body={
            <p className="m-0 text-body text-text-muted">
              This saves over the original view settings with the view you have
              on screen now — its columns, filters, sort and grouping.
              {' '}Anybody you shared it with sees the new one.
            </p>
          }
          onCancel={() => setUpdatingView(null)}
          onConfirm={() => void saveOverView(updatingView)}
        />
      )}

      {notice && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast tone={notice.tone} onDismiss={() => setNotice(null)}>{notice.text}</Toast>
        </div>
      )}

      {/*
        * What a bulk action did, and the one thing worth doing about it.
        *
        * Above `notice`'s toast rather than beside it, because the two cannot
        * both be about the same moment — a bulk action's result is the only
        * thing on the screen that matters while it is there.
        */}
      {toast && (
        <div className="fixed bottom-4 left-1/2 z-50 -translate-x-1/2">
          <Toast
            tone={toast.tone === 'warning' ? 'warn' : toast.tone}
            onDismiss={() => setToast(null)}
            {...(toast.action ? { action: toast.action } : {})}
          >
            {toast.text}
          </Toast>
        </div>
      )}

      {confirming && (() => {
        const spec = confirming.action.confirm?.(bulkCtx, confirming.input)
        const count = formatNumber(list.selectedCount)
        return (
          <ConfirmDialog
            title={spec?.title ?? `${confirming.action.label(list.selectedCount)}?`}
            tone={confirming.action.danger ? 'danger' : 'default'}
            busy={bulkBusy}
            confirmLabel={spec?.confirmLabel ?? `Yes, ${count}`}
            busyLabel="Working…"
            body={
              <div className="flex flex-col gap-2.5">
                {/*
                  * The number changed under us. Said plainly, with the new
                  * one, rather than as an error — the guard did its job.
                  */}
                {confirming.restated && (
                  <p className="m-0 rounded-card border border-warn/30 bg-warn-bg px-3 py-2 text-cell text-warn">
                    {confirming.restated} Have another look before you go on.
                  </p>
                )}
                {spec?.body ?? (
                  <p className="m-0 text-body text-text-muted">{scopeLine(bulkCtx)}.</p>
                )}
              </div>
            }
            onCancel={() => setConfirming(null)}
            onConfirm={() => {
              const { action, input } = confirming
              setConfirming(null)
              void runAction(action, input)
            }}
          />
        )
      })()}

      {/*
        * **See why.** "Moved 10 of 12" with no account of the other two is a
        * result nobody can act on, and the two that did not move are usually
        * the interesting ones.
        */}
      {skipped && (
        <Modal
          open
          onClose={() => setSkipped(null)}
          title={`${formatNumber(skipped.skipped.length)} didn’t change`}
          width={440}
        >
          <div className="flex flex-col gap-3 p-4">
            <p className="m-0 text-body text-text-muted">
              The other {formatNumber(skipped.changed)} did. Nothing half-happened.
            </p>
            <ul className="m-0 flex list-none flex-col gap-0 p-0">
              {skipped.skipped.map((row) => (
                <li
                  key={row.id}
                  className="flex items-baseline justify-between gap-3 border-b border-line-soft py-2 last:border-b-0"
                >
                  <span className="min-w-0 flex-1 truncate text-cell text-text">{row.label}</span>
                  <span className="shrink-0 text-sub text-warn">{row.reason}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={() => {
                list.keepOnly(skipped.skipped.map((row) => row.id))
                setSkipped(null)
              }}
              className="self-start text-cell font-semibold text-accent hover:text-accent-dark"
            >
              Keep these {formatNumber(skipped.skipped.length)} selected
            </button>
          </div>
        </Modal>
      )}

      {/* The same menu, as a sheet, raised by the phone's selection bar. */}
      {phone && phoneBulk && (
        <BulkMenu
          list={list}
          actions={bulkActions}
          ctx={bulkCtx}
          onChoose={(action, input) => { setPhoneBulk(false); void runAction(action, input) }}
          openNow
          onClose={() => setPhoneBulk(false)}
        />
      )}
    </>
  )
}

/**
 * How tall `PhoneSelectionBar` is, so the Back-to-top button can sit above
 * rather than behind it. One number, because two of them drift.
 */
const SELECTION_BAR = 72

/**
 * The exact opposite of an action, where one exists.
 *
 * Deliberately a short list. Undo is only honest where the reverse is a
 * single, complete operation — turning a flag back — and "unsend an email"
 * or "un-void an invoice" are not that. Everything else has the audit trail
 * (§4.2), which is the real record of what the old value was.
 */
const UNDO_OF: Record<string, string> = {
  deactivate: 'reactivate',
  reactivate: 'deactivate',
  /* Activity's Tasks (§8.1, T17): each the other's exact opposite. Names only — no list's behaviour moves. */
  mark_done: 'mark_not_done',
  mark_not_done: 'mark_done',
}

/**
 * What the confirm says before deactivating a selection.
 *
 * The `matching` branch is the one that matters. "Deactivate 126" over rows
 * somebody cannot all see is the one place this can do real damage, so the
 * box names the filter it is acting through, says how many were deliberately
 * left out, and says in as many words that it is **not** just the fifty on
 * screen — which is the assumption everybody makes about a bulk action and
 * the one this feature breaks on purpose.
 */
function DeactivateBody({ ctx, list }: { ctx: BulkContext; list: UseListResult }) {
  const sentence = describeFilter(ctx.view.filter, filterLabels(list))
  return (
    <div className="flex flex-col gap-2.5">
      {ctx.selection.mode === 'matching' && (
        <div className="flex gap-2 rounded-card border border-line bg-surface-alt px-3 py-2.5">
          <span aria-hidden className="mt-0.5 shrink-0 text-text-muted">
            <IconFilter size={14} />
          </span>
          <p className="m-0 text-cell text-text">
            <b>Every {nounFor({ ...ctx, count: 1 })} that matches</b>
            {sentence ? ` ${sentence}` : ' this list'}
            {ctx.selection.excluded.size > 0
              && `, except the ${formatNumber(ctx.selection.excluded.size)} you left out`}
            .{' '}
            <span className="text-text-muted">
              Not just the {formatNumber(ctx.rows.length)} on screen.
            </span>
          </p>
        </div>
      )}
      <p className="m-0 text-body text-text-muted">
        They drop out of this list and out of the pickers. Nothing is deleted —
        their history, their links and their work stay exactly where they are,
        and you can turn them back on by filtering to Active · No.
      </p>
    </div>
  )
}

/**
 * How tall a related list is: its header, then four rows.
 *
 * Four *rows* — the header used to be one of the four, so a list of four
 * things showed three and scrolled. The heights are the same tokens the table
 * draws with, so changing a row height in `design/tokens.json` moves this too.
 */
const RELATED_ROWS = 4
const RELATED_HEADER = 40

function relatedHeight(density: 'compact' | 'default' | 'spacious'): number {
  const row = density === 'compact' ? 44 : density === 'spacious' ? 60 : 52
  return RELATED_HEADER + row * RELATED_ROWS
}

function download(filename: string, csv: string): void {
  const url = URL.createObjectURL(new Blob([csv], { type: 'text/csv;charset=utf-8' }))
  const anchor = document.createElement('a')
  anchor.href = url
  anchor.download = filename
  anchor.click()
  URL.revokeObjectURL(url)
}

/*
 * `ImportDialog` and its `PreviewRow` lived here. They are
 * `components/smart-import.tsx` now — four steps instead of one, because an
 * import that goes straight from a file to "1,240 rows applied" is an import
 * nobody dares press. Plan §3.5's rule is unchanged and now has a screen to
 * stand on: the preview is the approval, and the batch the server applies is
 * the one that was shown.
 */
